import type { AudioClip, Project } from './types';

/**
 * Where a clip's audio is, in the order it is heard.
 *
 * A clip reads `[offsetSec, offsetSec + durationSec)` of its asset. Forwards,
 * the first thing heard is `offsetSec`; reversed, it is the far end of that
 * span, and every edit that moves an edge has to know which. Trimming the start
 * of a reversed clip cuts the *end* of its source — the part heard last is the
 * part that was recorded first — and a trim that moved `offsetSec` as if the
 * clip read forwards cut the wrong end, so the audio under the edge moved.
 *
 * Everything that recuts a clip — trim, split, slice, a crossfade reaching into
 * unused source — states what it wants in heard order and comes through here.
 * This is the one place that turns heard order back into source coordinates,
 * because two places would eventually disagree about which end is which.
 */

function assetEndSec(project: Project, clip: AudioClip): number {
  const asset = project.assets[clip.assetId];
  return asset ? asset.durationSec : clip.offsetSec + clip.durationSec;
}

/** Unused source before the first thing heard, in seconds. */
export function headroomBefore(project: Project, clip: AudioClip): number {
  if (clip.reversed) return Math.max(0, assetEndSec(project, clip) - (clip.offsetSec + clip.durationSec));
  return Math.max(0, clip.offsetSec);
}

/** Unused source after the last thing heard, in seconds. */
export function headroomAfter(project: Project, clip: AudioClip): number {
  if (clip.reversed) return Math.max(0, clip.offsetSec);
  return Math.max(0, assetEndSec(project, clip) - (clip.offsetSec + clip.durationSec));
}

/**
 * The clip recut to a span of what it plays: `leadSec` of heard audio dropped
 * from the front — negative to reach into unused source before it — and
 * `durationSec` kept from there. Clamped to the asset, so nothing can be asked
 * for that was never recorded; a caller that moves the clip's start has to
 * clamp its tick by `headroomBefore` for the same reason.
 */
export function recutSource(
  project: Project,
  clip: AudioClip,
  leadSec: number,
  durationSec: number,
): { offsetSec: number; durationSec: number } {
  const end = assetEndSec(project, clip);
  let from: number;
  let to: number;
  if (clip.reversed) {
    // Heard order runs from the far end of the span back towards `offsetSec`,
    // so the lead comes off the top and the duration counts down from there.
    to = clip.offsetSec + clip.durationSec - leadSec;
    from = to - durationSec;
  } else {
    from = clip.offsetSec + leadSec;
    to = from + durationSec;
  }
  from = Math.min(end, Math.max(0, from));
  to = Math.min(end, Math.max(from, to));
  return { offsetSec: from, durationSec: Math.max(0.001, to - from) };
}
